/** Files the picker offers; the extension rule also covers a browser that reports no type. */
export const JSON_ACCEPT = '.json,application/json';

/** Stored on the object so the engine fetches a document, whatever type the OS reported. */
export const JSON_CONTENT_TYPE = 'application/json';

/** The engine refuses a larger document and keeps the previous value, silently. */
export const JSON_DOCUMENT_MAX_BYTES = 1024 * 1024;

/**
 * Why a picked file cannot become a `json` value: the engine's two download
 * rules, or a document that does not parse.
 */
export type JsonDocumentProblem = 'tooLarge' | 'invalid' | 'notObject';

/**
 * Reads a picked file as text, through `FileReader` where `Blob.text()` is
 * missing (jsdom, older embedded browsers).
 *
 * @param file - File chosen in the picker.
 * @returns The file's contents.
 */
const readFileText = (file: File): Promise<string> => {
  if (typeof file.text === 'function') {
    return file.text();
  }

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(file);
  });
};

/**
 * Checks a picked file against what the engine accepts, before it is uploaded.
 *
 * Size first, without reading the file; the root check mirrors the engine,
 * which keeps only a JSON object and otherwise silently leaves the property
 * unchanged.
 *
 * @param file - File chosen in the picker.
 * @returns The problem, or `null` when the engine would accept the document.
 */
export const validateJsonDocument = async (
  file: File,
): Promise<JsonDocumentProblem | null> => {
  if (file.size > JSON_DOCUMENT_MAX_BYTES) {
    return 'tooLarge';
  }

  let parsed: unknown;

  try {
    parsed = JSON.parse(await readFileText(file));
  } catch {
    return 'invalid';
  }

  if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return 'notObject';
  }

  return null;
};
